import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { ToastrService } from 'ngx-toastr';
import { filter } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
    private readonly swUpdate = inject(SwUpdate);
    private readonly toastr = inject(ToastrService);
    private readonly router = inject(Router);
    private readonly startedAtMs = Date.now();
    private readonly notifyOnlyAfterMs = 15_000;
    private readonly checkEveryMs = 6 * 60 * 60 * 1000;
    private currentUrl = '/';
    private lastHandledHash: string | null = null;
    private isReloading = false;

    constructor() {
        this.start();
    }

    private start(): void {
        if (!this.swUpdate.isEnabled) {
            return;
        }

        this.currentUrl = this.router.url || '/';
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event) => {
                this.currentUrl = event.urlAfterRedirects || event.url || '/';
            });

        this.swUpdate.versionUpdates
            .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
            .subscribe((event) => this.activateVersion(event));

        this.checkForUpdate();
        window.setInterval(() => this.checkForUpdate(), this.checkEveryMs);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.checkForUpdate();
            }
        });
    }

    private activateVersion(event: VersionReadyEvent): void {
        const nextHash = event.latestVersion?.hash ?? null;
        if (this.isReloading || (nextHash && this.lastHandledHash === nextHash)) {
            return;
        }

        this.lastHandledHash = nextHash;
        if (Date.now() - this.startedAtMs >= this.notifyOnlyAfterMs && !this.isAuthPage()) {
            this.toastr.info('Atualização disponível. Atualizando agora…', 'Atualização', {
                timeOut: 3000,
                extendedTimeOut: 1000,
                closeButton: true,
                tapToDismiss: true,
            });
        }

        this.isReloading = true;
        void this.swUpdate
            .activateUpdate()
            .then(() => document.location.reload())
            .catch(() => {
                this.isReloading = false;
            });
    }

    private checkForUpdate(): void {
        void this.swUpdate.checkForUpdate().catch(() => undefined);
    }

    private isAuthPage(): boolean {
        const url = this.currentUrl.toLowerCase();
        return (
            url.includes('/sign-in') ||
            url.includes('/sign-up') ||
            url.includes('/forgot-password') ||
            url.includes('/reset-password') ||
            url.includes('/confirmation-required') ||
            url.includes('/not-found-company')
        );
    }

    checkNow(): void {
        if (this.swUpdate.isEnabled) {
            this.checkForUpdate();
        }
    }
}
